import { useCallback, useEffect, useState } from 'react';
import { Check, Copy, Plus, TicketPercent, Trash2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { couponsAPI } from '../../lib/api';
import { t } from '../../lib/i18n';
import { Field, Modal, btnGhost, btnPrimary, inputCls } from '../../components/ui';
import {
  Badge, DataTable, EmptyPanel, FilterPills, PageHeader, PersonCell, RowMenu, SearchBox,
  StatCards, TableRow, adminBtnPrimary, fmtDate,
} from '../../components/adminUi';

// Admin · Coupons (s136): single-use PERCENTAGE coupons. The admin types the %
// (and optionally the code); each coupon is consumed by the first order that
// carries it, and the row then shows who used it (name, last name, CA-N),
// when, and on which course(s).
const COLS = [
  { label: 'Code', width: 'minmax(0,1.5fr)' },
  { label: 'Discount', width: '100px' },
  { label: 'Status', width: '120px' },
  { label: 'Used by', width: 'minmax(0,2.2fr)' },
  { label: 'Course', width: 'minmax(0,1.4fr)' },
  { label: 'Date', width: '120px' },
  { label: 'Actions', width: '60px', align: 'right' },
];
const STATUS = {
  available: ['ok', 'Available'],
  reserved: ['info', 'In checkout'],
  used: ['neutral', 'Used'],
};
const PRESETS = [10, 20, 30, 50, 100];

function CodeCell({ code, note }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error(t('Could not copy'));
    }
  };
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="truncate font-mono text-[13.5px] font-bold tracking-wide text-lms-ink">{code}</span>
        <button
          type="button"
          onClick={copy}
          aria-label={t('Copy code')}
          title={t('Copy code')}
          className="lms-press flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-lms-sub hover:bg-lms-raised hover:text-lms-ink"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </span>
      {note && <span className="truncate text-xs text-lms-sub" title={note}>{note}</span>}
    </div>
  );
}

export default function CouponsPage() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [percent, setPercent] = useState('');
  const [code, setCode] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    couponsAPI.list({ status, search })
      .then(({ data: d }) => setData(d))
      .catch(() => toast.error(t('Could not load coupons')));
  }, [status, search]);
  useEffect(() => {
    const timer = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  const openCreate = () => {
    setPercent('');
    setCode('');
    setNote('');
    setCreating(true);
  };

  const pct = Number(percent);
  const pctValid = pct > 0 && pct <= 100;

  const create = async (e) => {
    e.preventDefault();
    if (!pctValid) return;
    setBusy(true);
    try {
      const { data: c } = await couponsAPI.create({ percent: pct, code, note });
      toast.success(`${t('Coupon created')}: ${c.code}`);
      setCreating(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    } finally {
      setBusy(false);
    }
  };

  const del = async (row) => {
    if (!window.confirm(`${t('Delete coupon')} ${row.code}?`)) return;
    try {
      await couponsAPI.remove(row.id);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Delete failed'));
    }
  };

  const release = async (row) => {
    try {
      await couponsAPI.release(row.id);
      toast.success(t('Coupon released'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    }
  };

  const sm = (data && data.summary) || null;

  return (
    <div className="flex flex-col gap-[22px]">
      <PageHeader title={t('Coupons')} subtitle={t('Single-use percentage coupons. Each code works once, and you can see who used it.')}>
        <button type="button" onClick={openCreate} className={adminBtnPrimary}>
          <Plus size={16} strokeWidth={2.4} /> {t('Create coupon')}
        </button>
      </PageHeader>

      {sm && (
        <StatCards
          items={[
            { label: t('Coupons'), value: sm.total },
            { label: t('Available'), value: sm.available },
            { label: t('In checkout'), value: sm.reserved, note: t('checkout opened, not paid yet') },
            { label: t('Used'), value: sm.used },
          ]}
        />
      )}

      <FilterPills
        value={status}
        onChange={setStatus}
        items={[
          { key: '', label: t('All'), count: sm ? sm.total : undefined },
          { key: 'available', label: t('Available'), count: sm ? sm.available : undefined },
          { key: 'used', label: t('Used'), count: sm ? sm.used : undefined },
        ]}
      >
        <SearchBox value={search} onChange={setSearch} placeholder={t('Search code, member or CA-…')} />
      </FilterPills>

      {!data ? (
        <p className="text-sm text-lms-muted">{t('Loading…')}</p>
      ) : (
        <DataTable
          cols={COLS}
          empty={data.coupons.length === 0 ? (
            <EmptyPanel bare icon={TicketPercent} title={search || status ? t('No coupons match these filters.') : t('No coupons yet.')}>
              {!search && !status && (
                <button type="button" onClick={openCreate} className={`${adminBtnPrimary} mt-3`}>
                  <Plus size={16} strokeWidth={2.4} /> {t('Create coupon')}
                </button>
              )}
            </EmptyPanel>
          ) : null}
        >
          {data.coupons.map((c) => {
            const [kind, label] = STATUS[c.status] || STATUS.available;
            const m = c.member;
            return (
              <TableRow key={c.id} cols={COLS}>
                <CodeCell code={c.code} note={c.note} />
                <span className="text-sm font-bold text-lms-ink lms-num">{c.percent}%</span>
                <Badge kind={kind}>{t(label)}</Badge>
                {m ? (
                  <PersonCell
                    name={`${m.first_name} ${m.last_name}`.trim() || m.email}
                    email={[m.member_code, m.email].filter(Boolean).join(' · ')}
                  />
                ) : c.checkout_member ? (
                  <span className="min-w-0 text-[13px] text-lms-sub">
                    <span className="block truncate">
                      {t('Checkout opened by')} {`${c.checkout_member.first_name} ${c.checkout_member.last_name}`.trim()}
                    </span>
                    <span className="block truncate text-xs text-lms-faint">{c.checkout_member.member_code} · {t('not paid yet')}</span>
                  </span>
                ) : (
                  <span className="text-[13px] text-lms-faint">—</span>
                )}
                <span className="truncate text-[13px] text-lms-ink" title={(c.courses || []).map((x) => x.title).join(', ')}>
                  {(c.courses || []).map((x) => x.title).join(', ') || <span className="text-lms-faint">—</span>}
                </span>
                <span className="flex flex-col text-[13px] text-lms-sub">
                  {c.status === 'used' ? fmtDate(c.used_at) : fmtDate(c.created_at)}
                  <span className="text-[11.5px] text-lms-faint">{c.status === 'used' ? t('used') : t('created')}</span>
                </span>
                <span className="flex justify-end">
                  {c.status !== 'used' ? (
                    <RowMenu items={[
                      ...(c.status === 'reserved' ? [{ label: t('Release'), icon: Undo2, onClick: () => release(c) }] : []),
                      { label: t('Delete'), icon: Trash2, onClick: () => del(c), danger: true },
                    ]} />
                  ) : <span />}
                </span>
              </TableRow>
            );
          })}
        </DataTable>
      )}

      {creating && (
        <Modal title={t('Create coupon')} onClose={() => setCreating(false)}>
          <form onSubmit={create} className="space-y-4">
            <Field label={t('Discount (%)')}>
              <div className="relative">
                <input
                  type="number"
                  min="1"
                  max="100"
                  step="1"
                  value={percent}
                  onChange={(e) => setPercent(e.target.value)}
                  placeholder="20"
                  autoFocus
                  required
                  className={`${inputCls} pr-12`}
                />
                <span className="pointer-events-none absolute right-8 top-1/2 -translate-y-1/2 text-sm text-lms-sub">%</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {PRESETS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPercent(String(p))}
                    className={`lms-press h-8 rounded-full border px-3 text-xs font-bold ${
                      Number(percent) === p ? 'border-lms-ink bg-lms-ink text-lms-surface' : 'border-lms-line text-lms-body hover:border-lms-ink'
                    }`}
                  >
                    {p}%
                  </button>
                ))}
              </div>
            </Field>
            <Field label={t('Code (optional)')}>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder={t('Leave empty to generate one')}
                maxLength={40}
                className={`${inputCls} font-mono uppercase placeholder:font-sans placeholder:normal-case`}
              />
            </Field>
            <Field label={t('Note (optional)')}>
              <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder={t('e.g. who it is for')} className={inputCls} />
            </Field>
            <p className="text-xs text-lms-sub">{t('The coupon can be used once, on any paid course. 100% makes the order free.')}</p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setCreating(false)} className={btnGhost}>{t('Cancel')}</button>
              <button type="submit" disabled={busy || !pctValid} className={btnPrimary}>
                {busy ? t('Saving…') : t('Create coupon')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
